import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Advert {
  id: string;
  title: string;
  tagline: string;
  description: string;
  url: string;
  cta: string;
  main_image: string | null;
  banner_image: string | null;
  sidebar_image: string | null;
}

interface AdPlaceholderProps {
  variant?: "sidebar" | "banner";
  className?: string;
}

export const AdPlaceholder = ({ variant = "sidebar", className = "" }: AdPlaceholderProps) => {
  const [adverts, setAdverts] = useState<Advert[]>([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAdverts();
  }, []);

  const fetchAdverts = async () => {
    try {
      const { data, error } = await supabase
        .from('adverts')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      setAdverts(data || []);
    } catch (error) {
      console.error('Error fetching adverts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-rotate ads every 8 seconds (optimal for conversion)
  useEffect(() => {
    if (adverts.length === 0) return;

    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % adverts.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [adverts.length]);

  const trackClick = async (advertId: string) => {
    try {
      await supabase.from('advert_clicks').insert({
        advert_id: advertId,
        page_url: window.location.pathname,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        user_id: (await supabase.auth.getUser()).data.user?.id || null,
      });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  if (isLoading || adverts.length === 0) {
    return null;
  }

  const currentAd = adverts[currentAdIndex];
  const imageUrl = variant === "banner" 
    ? (currentAd.banner_image || currentAd.main_image)
    : (currentAd.sidebar_image || currentAd.main_image);

  const handleClick = () => {
    trackClick(currentAd.id);
  };

  if (variant === "banner") {
    return (
      <a 
        href={currentAd.url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="block"
        onClick={handleClick}
      >
        <Card className={`p-6 hover-lift text-center overflow-hidden group relative ${className}`}>
          {imageUrl && (
            <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity">
              <img 
                src={imageUrl} 
                alt={currentAd.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="relative z-10">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-sm font-bold text-foreground">{currentAd.title}</span>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-xs font-medium text-foreground/80 mb-1">
              {currentAd.tagline}
            </p>
            <p className="text-xs text-muted-foreground">
              {currentAd.description}
            </p>
          </div>
        </Card>
      </a>
    );
  }

  return (
    <a 
      href={currentAd.url} 
      target="_blank" 
      rel="noopener noreferrer"
      className="block"
      onClick={handleClick}
    >
      <Card className={`overflow-hidden hover-lift group ${className}`}>
        <div className="aspect-square relative">
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={currentAd.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-muted" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent opacity-90" />
          <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-bold text-foreground">
                {currentAd.title}
              </p>
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              {currentAd.tagline}
            </p>
            <div className="inline-block px-2 py-1 bg-primary/20 rounded text-xs font-medium text-primary group-hover:bg-primary/30 transition-colors">
              {currentAd.cta}
            </div>
          </div>
        </div>
      </Card>
    </a>
  );
};
